// WHAT: Dashboard page component for Miracle Flow with stats and session access
// WHY:  Provides overview of Miracle Flow progress and quick access to sessions
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: useDashboardStats hook, React Query, modular dashboard components
// RISKS:
//   - NOTE: Multiple useEffect hooks for data loading - may cause redundant fetches
//   - NOTE: Base route detection logic is duplicated - may get out of sync
//   - NOTE: Active session detection may not be accurate

import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw, AlertTriangle } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Card, Button, Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

// NOTE: Import modular components
import { SessionCards, WeeklyProgressChart } from "../components/molecules";
import { DashboardHero, RecentActivity } from "../components/organisms";
import { useDashboardStats } from "../hooks/useDashboardStats";

/**
 * WHAT:
 *   Dashboard page component that displays Miracle Flow overview with statistics,
 *   session quick access, progress charts, and recent activity.
 *
 * WHY:
 *   Provides central hub for Miracle Flow users to see progress and start sessions.
 *
 * BEHAVIOR:
 *   - Loads dashboard stats on mount and route changes
 *   - Displays key metrics (streak, completion rate, sessions)
 *   - Shows active session warning if applicable
 *   - Provides quick access to morning/evening sessions
 *   - Displays weekly progress chart
 *   - Shows recent activity
 *   - Supports manual refresh
 *
 * RISKS:
 *   - NOTE: Multiple useEffect hooks for data loading - may cause redundant fetches
 *   - NOTE: Base route detection logic is duplicated - may get out of sync
 *   - NOTE: Active session detection may not be accurate
 *
 * @returns MiracleFlowDashboardTab component
 *
 */
export const MiracleFlowDashboardTab: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [hasActiveSession] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedReference = useRef(false);
  const lastPathnameReference = useRef<string>("");

  /**
   * WHAT:
   *   Extracts base route from current pathname.
   *
   * WHY:
   *   Supports multiple routing scenarios (direct and nested routes).
   *
   * @returns Base route string
   *
   */
  const getBaseRoute = () => {
    const pathname = location.pathname;
    if (pathname.startsWith("/nexus/personal-development/miracle-flow")) {
      return "/nexus/personal-development/miracle-flow";
    }
    return "/miracle-flow";
  };

  const baseRoute = getBaseRoute();

  // NOTE: Use real dashboard stats - will automatically refetch on mount due to refetchOnMount: 'always'
  const { data: dashboardStats, refetch: refetchStats } = useDashboardStats();

  /**
   * WHAT:
   *   Manually loads dashboard data with loading state.
   *
   * WHY:
   *   Enables user-triggered refresh of dashboard statistics.
   */
  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // NOTE: Invalidate and refetch to ensure fresh data
      await queryClient.invalidateQueries({
        queryKey: ["miracle-flow", "dashboard-stats"],
      });
      await refetchStats();
    } catch {
      // NOTE: Failed to load dashboard data
    } finally {
      setIsLoading(false);
    }
  };

  // NOTE: Load data on initial mount
  useEffect(() => {
    const pathname = location.pathname;
    const isDashboardRoute =
      pathname === "/miracle-flow" ||
      pathname === "/miracle-flow/" ||
      pathname === "/nexus/personal-development/miracle-flow" ||
      pathname === "/nexus/personal-development/miracle-flow/";

    if (isDashboardRoute && !hasLoadedReference.current) {
      hasLoadedReference.current = true;
      queryClient
        .invalidateQueries({
          queryKey: ["miracle-flow", "dashboard-stats"],
        })
        .catch(() => {
          // Query invalidation errors are handled silently
        });
      refetchStats().catch(() => {
        // Refetch errors are handled silently
      });
    }

    // Reason: Effect should only run once on mount, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Reason: Effect should only run once on mount, dependencies are intentionally omitted
  }, []); // NOTE: Only on mount

  // NOTE: Load data when pathname changes to dashboard route
  useEffect(() => {
    const pathname = location.pathname;
    const isDashboardRoute =
      pathname === "/miracle-flow" ||
      pathname === "/miracle-flow/" ||
      pathname === "/nexus/personal-development/miracle-flow" ||
      pathname === "/nexus/personal-development/miracle-flow/";
    const pathnameChanged = lastPathnameReference.current !== pathname;

    // NOTE: Update ref
    lastPathnameReference.current = pathname;

    if (isDashboardRoute && pathnameChanged) {
      // NOTE: Pathname changed to dashboard route - refetch data
      queryClient
        .invalidateQueries({
          queryKey: ["miracle-flow", "dashboard-stats"],
        })
        .catch(() => {
          // Query invalidation errors are handled silently
        });
      refetchStats().catch(() => {
        // Refetch errors are handled silently
      });
    }
  }, [location.pathname, queryClient, refetchStats]);

  // NOTE: Also check on every render if we're on dashboard and data hasn't been loaded
  // This handles the case where component doesn't remount but is visible
  useEffect(() => {
    const pathname = location.pathname;
    const isDashboardRoute =
      pathname === "/miracle-flow" ||
      pathname === "/miracle-flow/" ||
      pathname === "/nexus/personal-development/miracle-flow" ||
      pathname === "/nexus/personal-development/miracle-flow/";

    if (isDashboardRoute && !dashboardStats && !isLoading) {
      // NOTE: We're on dashboard but have no data - load it
      queryClient
        .invalidateQueries({
          queryKey: ["miracle-flow", "dashboard-stats"],
        })
        .catch(() => {
          // Query invalidation errors are handled silently
        });
      refetchStats().catch(() => {
        // Refetch errors are handled silently
      });
    }
  }, [location.pathname, dashboardStats, isLoading, queryClient, refetchStats]);

  return (
    <div className="space-y-8 p-6">
      {/* Header with Refresh */}
      <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
        <Stack align="center" direction="row" justify="between">
          <Stack direction="col" gap={1}>
            <Heading className="text-[#F1F5F9] font-bold" level="h2" size="xl">
              Miracle Flow Dashboard
            </Heading>
            <p className="text-[#94A3B8]">Your daily routine for success and personal growth</p>
          </Stack>
          <Button
            className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
            disabled={isLoading}
            onClick={() => {
              loadDashboardData().catch(() => {
                // Dashboard data loading errors are handled silently
              });
            }}
            variant="outline"
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")} />
            {isLoading ? "Loading..." : "Refresh"}
          </Button>
        </Stack>
      </Card>

      {/* Active Session Warning */}
      {hasActiveSession ? (
        <Card
          className="bg-tracq-light-orange/10 border-tracq-light-orange/20"
          padding="md"
          variant="default"
        >
          <Stack align="center" direction="row" justify="between">
            <Stack align="center" direction="row" gap={3}>
              <AlertTriangle className="w-6 h-6 text-tracq-light-orange" />
              <Stack direction="col" gap={1}>
                <Heading className="text-tracq-light-orange" level="h3" size="sm">
                  Active Session Running
                </Heading>
                <p className="text-[#CBD5E1] text-sm">
                  You already have a session started. Resume it to continue your progress.
                </p>
              </Stack>
            </Stack>
            <Button
              className="border-tracq-light-orange/50 text-tracq-light-orange hover:bg-tracq-light-orange/10"
              onClick={() => {
                const result = navigate(`${baseRoute}/morning-session`);
                if (result instanceof Promise) {
                  result.catch(() => {
                    // Navigation errors are handled silently
                  });
                }
              }}
              size="sm"
              variant="outline"
            >
              Resume Session
            </Button>
          </Stack>
        </Card>
      ) : null}

      {/* Hero Section with Key Metrics */}
      {dashboardStats ? (
        <DashboardHero
          completionRate={dashboardStats.completion_rate || 0}
          nextMilestone={dashboardStats.next_goal_milestone}
          sessionsUntilMilestone={dashboardStats.sessions_until_milestone}
          streak={dashboardStats.current_streak || 0}
          thisWeekSessions={dashboardStats.this_week_sessions || 0}
          totalSessions={dashboardStats.total_sessions || 0}
        />
      ) : null}

      {/* Session Quick Access */}
      <div>
        <Stack direction="col" gap={4}>
          <Heading className="text-[#F1F5F9] font-semibold" level="h3" size="lg">
            Start Your Session
          </Heading>
          <SessionCards
            hasActiveSession={hasActiveSession}
            onNavigate={(route) => {
              const result = navigate(`${baseRoute}/${route}`);
              if (result instanceof Promise) {
                result.catch(() => {
                  // Navigation errors are handled silently
                });
              }
            }}
          />
        </Stack>
      </div>

      {/* Progress & Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Progress Chart */}
        <WeeklyProgressChart />

        {/* Recent Activity */}
        <RecentActivity
          hasActiveSession={hasActiveSession}
          {...(dashboardStats?.last_session_date
            ? { lastSessionDate: dashboardStats.last_session_date }
            : {})}
          onResumeSession={() => {
            const result = navigate(`${baseRoute}/morning-session`);
            if (result instanceof Promise) {
              result.catch(() => {
                // Navigation errors are handled silently
              });
            }
          }}
          onStartSession={() => {
            const result = navigate(`${baseRoute}/morning-session`);
            if (result instanceof Promise) {
              result.catch(() => {
                // Navigation errors are handled silently
              });
            }
          }}
          onViewHistory={() => {
            const result = navigate(`${baseRoute}/session-history`);
            if (result instanceof Promise) {
              result.catch(() => {
                // Navigation errors are handled silently
              });
            }
          }}
        />
      </div>

      {/* Additional Stats Grid (if needed) */}
      {dashboardStats ? (
        <div>
          <Stack direction="col" gap={4}>
            <Heading className="text-[#F1F5F9] font-semibold" level="h3" size="lg">
              Detailed Statistics
            </Heading>
            {/* Additional detailed stats can go here */}
          </Stack>
        </div>
      ) : null}
    </div>
  );
};
